import { useEffect, useState, useCallback, useRef } from "react";
import { styled } from "@mui/material/styles";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import { BrowserRouter } from "react-router-dom";
import { Amplify } from "aws-amplify";
import awsExports from "./config/aws-exports"; //운영과 개발서버 관리 해야함
import "./App.css";
import * as AuthMain from "./auth/AuthMain";
import { userState } from "./interface/MainInterface";
import Header from "./mainContainer/Header";
import MainConteiner from "./mainContainer/MainConteiner";
import { HttpMainApi } from "./interface/main-api";
Amplify.configure(awsExports);

let userParam: userState = {
  id: "",
  name: "",
  company: "",
  dept: "",
  isAuth: false,
  isAdmin: false,
  isSuperAdmin: false,
  apiUrl: "",
  dtype: "",
};

const largeDrawerWidth: number = 250;
const zeroDrawerWidth: number = 0;
const appFontFamily = '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans KR", "Apple SD Gothic Neo", Arial, sans-serif';

const LayoutDivider = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  minHeight: "44px !important",
  padding: theme.spacing(0, 0),
  justifyContent: "flex-end",
}));

const theme = createTheme({
  typography: {
    fontFamily: appFontFamily,
  },
  palette: {
    // primary: {
    //   main: "#527FED",
    //   contrastText: "#fff",
    // },
    error: {
      main: "#ff000090",
      contrastText: "#fff",
    },
  },
  zIndex: { drawer: 990, modal: 990 },
});

function App() {
  const headerRef: any = useRef();
  const [userState, setUserState] = useState<userState>();
  const [selectedMenu, setSelectedMenu] = useState("");
  const [headerLarge, setHeaderLarge] = useState(true);
  const [desktopDrawerHovered, setDesktopDrawerHovered] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [searchResult, setSearchResult] = useState<any>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const winSize = isMobile ? "xs" : "lg";
  const isPublicMenu =
    selectedMenu === "/signin" ||
    selectedMenu === "/" ||
    selectedMenu === "/signup" ||
    selectedMenu === "/hartlogin" ||
    selectedMenu === "/haiSsoLogin" ||
    selectedMenu === "/unapproved";
  const hasNavigation = selectedMenu !== "" && !isPublicMenu;
  const desktopDrawerOpen = headerLarge || desktopDrawerHovered;
  const drawerWidth =
    hasNavigation && (isMobile || desktopDrawerOpen)
      ? largeDrawerWidth
      : zeroDrawerWidth;
  const contentMargin =
    hasNavigation && !isMobile && desktopDrawerOpen
      ? largeDrawerWidth
      : zeroDrawerWidth;
  const drawerOpen = isMobile ? mobileDrawerOpen : hasNavigation && desktopDrawerOpen;

  async function setUserInfo() {
    // 세션체크
    const result = AuthMain.getSession();
    if (result !== null) {
      userParam.isAuth = true;
      userParam.id = result.id;
      userParam.name = result.name;
      userParam.company = result.company;
      userParam.dept = result.dept;
      userParam.isAdmin = result.isAdmin;
      userParam.isSuperAdmin = result.isSuperAdmin;
      userParam.dtype = result.dtype;
    } else {
      const result = await AuthMain.checkAuthenticated();
      if (result) {
        // 사용자 정보 조회
        const userAttr = await AuthMain.getUserAttr();
        userParam.id = userAttr.email;
        userParam.name = userAttr.name;
        userParam.company = userAttr.company;
        // 어드민 여부 체크
        const adminInfo = await AuthMain.checkAdmin();
        userParam.isAuth = adminInfo.isAuth;
        userParam.isAdmin = adminInfo.isAdmin;
        userParam.isSuperAdmin = adminInfo.isSuperAdmin;
        userParam.dtype = adminInfo.dtype;
      }
    }
    console.log(
      "%cWELCOME %cHAAP",
      "color: blue; font-size:20px; font-weight: bold; text-shadow: 2px 2px 0px rgba(0,0,0,0.2);",
      "color: red; font-size:20px; font-weight: bold; text-shadow: 2px 2px 0px rgba(0,0,0,0.2);"
    );
    setUserState(userParam);
  }

  useEffect(() => {
    setUserInfo();
  }, []);

  useEffect(() => {
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [isMobile]);

  const handleDrawer = useCallback(() => {
    if (isMobile) {
      setMobileDrawerOpen((isOpen) => !isOpen);
    } else {
      setHeaderLarge((isLarge) => !isLarge);
      setDesktopDrawerHovered(false);
    }
  }, [isMobile]);

  const closeMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const menuOver = () => {
    if (!isMobile && !headerLarge) {
      setDesktopDrawerHovered(true);
    }
  };

  const menuOut = () => {
    if (!isMobile && !headerLarge) {
      setDesktopDrawerHovered(false);
    }
  };

  const getLeftCount = () => {
    headerRef.current.getLeftCount();
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        {userState !== undefined && (
          <BrowserRouter>
            {isPublicMenu ? (
              <></>
            ) : userState.isAuth ? (
              <>
                <Header
                  ref={headerRef}
                  userState={userState}
                  open={drawerOpen}
                  winSize={winSize}
                  selectedMenu={selectedMenu}
                  headerLarge={headerLarge}
                  drawerWidth={drawerWidth}
                  contentMargin={contentMargin}
                  largeDrawerWidth={largeDrawerWidth}
                  handleDrawer={handleDrawer}
                  closeMobileDrawer={closeMobileDrawer}
                  menuOver={menuOver}
                  menuOut={menuOut}
                />
                <LayoutDivider />
                {hasNavigation && !isMobile && !headerLarge && !desktopDrawerHovered && (
                  <Box
                    aria-hidden="true"
                    data-testid="desktop-collapsed-hover-rail"
                    onMouseEnter={menuOver}
                    sx={{
                      position: "fixed",
                      top: "44px",
                      bottom: 0,
                      left: 0,
                      width: "24px",
                      zIndex: theme.zIndex.drawer - 1,
                    }}
                  />
                )}
              </>
            ) : (
              <></>
            )}

            <MainConteiner
              userState={userState}
              drawerWidth={contentMargin}
              setSelectedMenu={setSelectedMenu}
              winSize={winSize}
              getLeftCount={getLeftCount}
              searchResult={searchResult}
              setSearchResult={setSearchResult}
            />
          </BrowserRouter>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
